/**
 * Home: start an estimate, continue recent work, open popular calculators
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardActionArea, Grid, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import RestoreRoundedIcon from '@mui/icons-material/RestoreRounded';
import { IconTile, Page } from '../ui';
import EstimateModeCards from '../estimate/EstimateModeCards';
import { describeInputs } from '../estimate/EstimateResultView';
import { getEstimateCopy } from '../estimate/copy';
import { ProjectRow } from '../projects/ProjectsPage';
import { CalculatorCard } from '../calculators/CalculatorsPage';
import { findCalculator, getRecentCalculators } from '../calculators/calculatorMeta';
import { useLanguage } from '../../utils/LanguageContext';
import { useAuth } from '../../utils/authUtils';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useEstimate } from '../../contexts/EstimateContext';
import { getProjects } from '../../utils/projectStorage';
import { formatCurrency } from '../../utils/format';

const POPULAR_CALCULATORS = ['bricks', 'plaster', 'tiles', 'slab', 'screed', 'metalSheet'];

const text = {
  en: {
    hello: 'Hi',
    guestHello: 'Welcome to RumahKira',
    title: 'How much will your house cost?',
    subtitle: 'Estimate the cost and materials for building a house in Malaysia.',
    continue: 'Continue where you left off',
    recentProjects: 'Recent projects',
    seeAll: 'See all',
    calculators: 'Material calculators',
    popular: 'Popular',
    yourRecent: 'Recently used',
    guestTitle: 'Save your estimates',
    guestDesc: 'Create a free account to save projects and get more free estimates every day.',
    signUp: 'Create free account',
    freeTitle: 'Need more?',
    freeDesc: 'Access Passes{from} unlock unlimited estimates, PDF reports and comparisons. Pay once, no subscription.',
    viewPasses: 'View passes',
    passActive: 'active',
    timeLeft: 'left',
    manage: 'Manage',
    by: 'RumahKira™ by Kondingau Community Insight',
  },
  my: {
    hello: 'Hai',
    guestHello: 'Selamat datang ke RumahKira',
    title: 'Berapa kos membina rumah anda?',
    subtitle: 'Anggar kos dan bahan untuk membina rumah di Malaysia.',
    continue: 'Sambung dari tadi',
    recentProjects: 'Projek terkini',
    seeAll: 'Lihat semua',
    calculators: 'Kalkulator bahan',
    popular: 'Popular',
    yourRecent: 'Baru digunakan',
    guestTitle: 'Simpan anggaran anda',
    guestDesc: 'Cipta akaun percuma untuk simpan projek dan dapat lebih banyak anggaran percuma setiap hari.',
    signUp: 'Cipta akaun percuma',
    freeTitle: 'Perlukan lebih?',
    freeDesc: 'Pas Akses{from} membuka anggaran tanpa had, laporan PDF dan perbandingan. Bayar sekali, tiada langganan.',
    viewPasses: 'Lihat pas',
    passActive: 'aktif',
    timeLeft: 'lagi',
    manage: 'Urus',
    by: 'RumahKira™ oleh Kondingau Community Insight',
  },
};

const SectionHeading = ({ title, action }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
    <Typography variant="h6" component="h2">
      {title}
    </Typography>
    {action}
  </Stack>
);

const PassBanner = ({ t, language }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasActivePass, getPassName, getTimeRemainingText, loading, minPassPrice } = useSubscription();

  if (user && loading) return null;

  if (user && hasActivePass) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardActionArea onClick={() => navigate('/app/account')} sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <IconTile icon={<WorkspacePremiumRoundedIcon />} color="success" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1">
                {getPassName(language)} {t.passActive}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getTimeRemainingText(language)}
              </Typography>
            </Box>
            <Typography variant="body2" color="primary" fontWeight={600}>
              {t.manage}
            </Typography>
          </Stack>
        </CardActionArea>
      </Card>
    );
  }

  const guest = !user;
  return (
    <Card
      sx={(theme) => ({
        mb: 3,
        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.1 : 0.04),
        borderColor: alpha(theme.palette.primary.main, 0.3),
      })}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
          <IconTile icon={guest ? <PersonAddAlt1RoundedIcon /> : <WorkspacePremiumRoundedIcon />} />
          <Box>
            <Typography variant="subtitle1">{guest ? t.guestTitle : t.freeTitle}</Typography>
            <Typography variant="body2" color="text.secondary">
              {guest ? t.guestDesc : t.freeDesc.replace('{from}', minPassPrice ? ` ${language === 'my' ? 'dari' : 'from'} RM${minPassPrice}` : '')}
            </Typography>
          </Box>
        </Stack>
        <Button
          variant={guest ? 'contained' : 'outlined'}
          onClick={() => navigate(guest ? '/auth?mode=signup' : '/app/account')}
          sx={{ flexShrink: 0 }}
        >
          {guest ? t.signUp : t.viewPasses}
        </Button>
      </Stack>
    </Card>
  );
};

const HomeDashboard = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth();
  const { result } = useEstimate();
  const t = text[language] || text.en;
  const et = getEstimateCopy(language);
  const [projects] = useState(() => getProjects().slice(0, 3));
  const [recentCalcs] = useState(getRecentCalculators);

  const calculators = recentCalcs.length >= 3 ? recentCalcs.slice(0, 6) : POPULAR_CALCULATORS.map(findCalculator).filter(Boolean);
  const firstName = user?.name ? user.name.split(' ')[0] : null;

  return (
    <Page maxWidth="lg">
      <Box sx={{ mb: 3 }}>
        <Typography variant="body1" color="text.secondary" fontWeight={500}>
          {firstName ? `${t.hello}, ${firstName}` : t.guestHello}
        </Typography>
        <Typography variant="h3" component="h1" sx={{ mt: 0.5, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
          {t.title}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 0.75 }}>
          {t.subtitle}
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <EstimateModeCards />
      </Box>

      {result && !result.id && (
        <Card sx={{ mb: 3 }}>
          <CardActionArea onClick={() => navigate('/app/estimate/result')} sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <IconTile icon={<RestoreRoundedIcon />} color="info" />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">
                  {t.continue}
                </Typography>
                <Typography variant="subtitle1" noWrap>
                  {formatCurrency(result.summary.totalCost.avg)} · {result.inputs.projectName || et.resultTitle}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {describeInputs(result.inputs, et, language)}
                </Typography>
              </Box>
              <ArrowForwardRoundedIcon color="primary" />
            </Stack>
          </CardActionArea>
        </Card>
      )}

      <PassBanner t={t} language={language} />

      <Grid container spacing={3}>
        {projects.length > 0 && (
          <Grid item xs={12} md={6}>
            <SectionHeading
              title={t.recentProjects}
              action={
                <Button size="small" endIcon={<ArrowForwardRoundedIcon />} onClick={() => navigate('/app/projects')}>
                  {t.seeAll}
                </Button>
              }
            />
            <Stack spacing={1.5}>
              {projects.map((p) => (
                <ProjectRow key={p.id} project={p} language={language} onClick={() => navigate(`/app/projects/${p.id}`)} />
              ))}
            </Stack>
          </Grid>
        )}

        <Grid item xs={12} md={projects.length > 0 ? 6 : 12}>
          <SectionHeading
            title={`${t.calculators} · ${recentCalcs.length >= 3 ? t.yourRecent : t.popular}`}
            action={
              <Button size="small" endIcon={<ArrowForwardRoundedIcon />} onClick={() => navigate('/app/calculators')}>
                {t.seeAll}
              </Button>
            }
          />
          <Grid container spacing={1.5}>
            {calculators.map((c) => (
              <Grid item xs={12} sm={6} md={projects.length > 0 ? 6 : 4} key={c.id}>
                <CalculatorCard calc={c} language={language} onClick={() => navigate(`/app/calculators/${c.id}`)} />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      <Typography variant="caption" color="text.disabled" component="p" sx={{ textAlign: 'center', mt: 5 }}>
        {t.by}
      </Typography>
    </Page>
  );
};

export default HomeDashboard;
